import { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import { Search } from "lucide-react";
import Header from "../Components/Header";
import { useTheme, Box } from "@mui/material";
import StudentSidebar from "./StudentSdebar";

const ITEMS_PER_PAGE = 5;

export default function FeedsPage() {
  const [feedItems, setFeedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All Updates");
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const token = localStorage.getItem("jwttoken");
  const theme = useTheme();

  useEffect(() => {
    loadFeedItems();
  }, []);

  const loadFeedItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://tdtlworld.com/dss-kaas-backend/socialapp/news_feed/`,
        {
          params: { page: currentPage, limit: ITEMS_PER_PAGE },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.length > 0) {
        setFeedItems((prev) => [...prev, ...response.data]);
        setCurrentPage((prev) => prev + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching feed items:", error);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredItems = feedItems.filter(
    (item) =>
      item.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.group?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <Header />
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? "calc(100% - 260px)" : "calc(100% - 80px)",
            transition: theme.transitions.create("width", { duration: 300 }),
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          <Container>
            <Row className="mb-4 align-items-center">
              <Col>
                <h1
                  className="fw-bold"
                  style={{ color: theme.palette.primary.main }}
                >
                  News Feed
                </h1>
              </Col>
            </Row>

            <Row className="mb-4 position-relative">
              <Col md={4} style={{ position: "relative" }}>
                <Form.Control
                  type="search"
                  placeholder="Search Feed..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="ps-5 py-2 rounded-pill shadow-sm border-0 search-input"
                  style={{
                    maxWidth: "350px",
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                />
                <Search
                  className="position-absolute top-50 start-0 translate-middle-y ms-3"
                  size={20}
                  style={{ color: theme.palette.text.secondary }}
                />
                <style>{`
                  .search-input::placeholder {
                    color: ${theme.palette.text.secondary};
                    opacity: 0.8;
                  }
                  .search-input:focus {
                    background-color: ${theme.palette.background.paper} !important;
                    color: ${theme.palette.text.primary} !important;
                    box-shadow: 0 0 0 0.25rem ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'} !important;
                  }
                `}</style>
              </Col>
            </Row>

            {loading && feedItems.length === 0 ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : filteredItems.length > 0 ? (
              <Row>
                <Col>
                  {filteredItems.map((item) => (
                    <Card
                      key={item.id}
                      className="mb-3 border-0 shadow-sm"
                      style={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Card.Body>
                        <div className="d-flex align-items-start mb-2">
                          <div>
                            <strong>User {item.user}</strong> {item.action} in
                            Group {item.group}
                            <div
                              className="small mt-2"
                              style={{ color: theme.palette.text.secondary }}
                            >
                              {new Date(item.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}

                  {hasMore && (
                    <div className="text-center my-4">
                      <Button
                        onClick={loadFeedItems}
                        disabled={loading}
                        className="px-4 border-0"
                        style={{
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          fontWeight: "bold",
                        }}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Loading...
                          </>
                        ) : (
                          "Load More"
                        )}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            ) : (
              <Row>
                <Col className="text-center py-5">
                  <h4
                    style={{
                      color:
                        theme.palette.mode === "dark"
                          ? "#FFFFFF"
                          : theme.palette.primary.main,
                    }}
                  >
                    No feed items found
                  </h4>
                  <p>Try adjusting your search or filters</p>
                </Col>
              </Row>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
